import React, { useState, useEffect, useContext } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Delete from '@mui/icons-material/Delete';
import MoreVert from '@mui/icons-material/MoreVert';
import Close from '@mui/icons-material/Close';
import Info from '@mui/icons-material/Info';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';

import { AxiosResponse } from 'axios';
import axiosInstance from '../../../utils/axiosIntercepter';
import { UserContext } from '../../../App';

interface ISubmenu {
    name: string;
    structures: Array<string>;
    tooltip?: string;
}

interface IUnordered {
    id: string;
    name?: string;
}

interface ISelectUnorder extends IUnordered {
    index: number;
}

export interface IProps {
    sidebarUpdater: () => void;
}

const Submenus: React.FC<IProps> = ({ sidebarUpdater }: IProps) => {
    const [submenus, setSubmenus] = useState<Array<ISubmenu>>([]);
    const [newSubmenu, setNewSubmenu] = useState<string>('');
    const [unsorted, setUnsorted] = useState<Array<IUnordered>>([]);
    const [allStructures, setAllStructures] = useState<Array<IUnordered>>([]);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [selectUnorder, setSelectUnorder] = useState<ISelectUnorder | undefined>();
    const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
    const [tooltipEditIndex, setTooltipEditIndex] = useState<number>(-1);
    const [tooltipInput, setTooltipInput] = useState<string>('');

    const userContext = useContext(UserContext);

    const initialCall = async () => {
        if (userContext.platform) {
            const axiosResponse = await axiosInstance.get(
                `/api/platforms/preferences/menus/${userContext.platform}`
            );
            const unorderedResponse: AxiosResponse<any> = await axiosInstance.get(
                `/api/platforms/${userContext.platform}/bases`
            );
            setSubmenus(() => axiosResponse.data);
            if (unorderedResponse.data.unordered) {
                setUnsorted(() => unorderedResponse.data.unordered);
            }
            if (unorderedResponse.data.structures) {
                setAllStructures(() => unorderedResponse.data.structures);
            }
        }

        sidebarUpdater();
    };

    const pushExample = async () => {
        await axiosInstance.post(
            `/api/platforms/preferences/menus/${userContext.platform}`,
            {
                data: {
                    name: newSubmenu
                }
            }
        );
        setNewSubmenu('');
        initialCall();
    };

    const removeSubmenu = async (name: string) => {
        await axiosInstance.delete(
            `/api/platforms/preferences/menus/${userContext.platform}/${name}`
        );
        initialCall();
    };

    const handleTextInputChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setNewSubmenu(() => event.target.value);
    };

    const handleTooltipInputChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setTooltipInput(() => event.target.value);
    };

    const handleDialogOpen = (id: string, index: number) => {
        setSelectUnorder(() => ({ id, index }));
        setDialogOpen(true);
    };

    const getStructureName = (id: string): string => {
        const structure = allStructures.find((value: IUnordered) => (
            value.id === id
        ));
        if (structure && structure.name) {
            return structure.name;
        }
        return '';
    };

    const handleClose = () => {
        setSelectUnorder(() => (undefined));
        setDialogOpen(false);
    };

    const handleTooltipClose = () => {
        setTooltipInput('');
        setTooltipOpen(false);
        setTooltipEditIndex(-1);
    };

    const handleOpenTooltip = (index: number) => {
        setTooltipEditIndex(index);
        if (submenus[index] && submenus[index].tooltip) {
            setTooltipInput(submenus[index].tooltip!);
        }
        setTooltipOpen(true);
    };

    const handlePersistTooltip = async () => {
        if (tooltipEditIndex >= 0) {
            const submenu = submenus[tooltipEditIndex];
            submenu.tooltip = tooltipInput;

            await axiosInstance.put(
                `/api/platforms/${userContext.platform}/preferences/submenus`,
                { data: submenus }
            );
        }
        handleTooltipClose();
    };

    const handleSelectSubmenu = async (index: number) => {
        const { structures } = submenus[index];
        if (selectUnorder) {
            structures.push(selectUnorder.id);
        }

        await axiosInstance.put(
            `/api/platforms/${userContext.platform}/preferences/submenus`,
            { data: submenus }
        );

        initialCall();

        handleClose();
    };

    const handleSetUnorder = async (submenuIndex: number, structureIndex: number) => {
        const { structures } = submenus[submenuIndex];

        structures.splice(structureIndex, 1);

        await axiosInstance.put(
            `/api/platforms/${userContext.platform}/preferences/submenus`,
            { data: submenus }
        );

        initialCall();
    };

    useEffect(() => {
        if (userContext.platform) {
            initialCall();
        }
    }, [userContext]);

    return (

        <Box sx={{ width: '100%' }}>
            <Paper elevation={4} sx={{ padding: '1rem 0', margin: '0.5rem 0' }}>
                <Typography variant="h4" align="center">
                    Submenús
                </Typography>
            </Paper>

            <Grid container spacing={3} direction="row">
                <Grid
                    item
                    xs={12}
                    lg={3}
                >
                    <Paper
                        elevation={2}
                        sx={{
                            padding: '1rem',
                            position: 'relative'
                        }}
                    >
                        <Paper elevation={1}>
                            <Typography
                                variant="h5"
                                align="center"
                                gutterBottom
                            >
                                Secciones
                            </Typography>
                        </Paper>
                        <List>
                            { submenus && submenus.map((submenu: ISubmenu, index: number) => (
                                <ListItem
                                    key={submenu.name}
                                    secondaryAction={(
                                        <>
                                            <IconButton
                                                aria-label="remove"
                                                onClick={() => { removeSubmenu(submenu.name); }}
                                            >
                                                <Delete />
                                            </IconButton>
                                            <IconButton
                                                aria-label="info"
                                                onClick={() => {
                                                    handleOpenTooltip(index);
                                                }}
                                            >
                                                <Info />
                                            </IconButton>
                                        </>

                                    )}
                                >
                                    <Typography sx={{
                                        width: '80%',
                                        overflowWrap: 'break-word'
                                    }}
                                    >
                                        {submenu.name}
                                    </Typography>

                                </ListItem>
                            ))}
                        </List>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Nuevo"
                            sx={{
                                margin: '0.5rem 0'
                            }}
                            value={newSubmenu}
                            onChange={handleTextInputChange}
                        />
                        <Box sx={{ textAlign: 'center' }}>
                            <Button
                                variant="contained"
                                onClick={pushExample}
                            >
                                Guardar
                            </Button>
                        </Box>
                    </Paper>
                </Grid>
                <Grid
                    item
                    xs={12}
                    lg={9}
                >
                    <Paper elevation={2} sx={{ padding: '1rem', height: '100%' }}>
                        <Paper elevation={1}>
                            <Typography
                                variant="h5"
                                align="center"
                                gutterBottom
                            >
                                Orden
                            </Typography>
                        </Paper>

                        { submenus.map((menu: ISubmenu, indexMenu: number) => (
                            <Box key={`Submenu-${menu.name}-${indexMenu.toString()}`}>
                                { menu.structures.length > 0
                                    && (

                                        <Paper
                                            sx={{
                                                padding: '1rem',
                                                marginBottom: '1rem'
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                align="left"
                                                gutterBottom
                                            >
                                                {menu.name}
                                            </Typography>
                                            <List>
                                                { menu.structures.map((
                                                    id: string, index: number
                                                ) => (
                                                    <ListItem
                                                        key={`SubMenuItem-${index.toString}${indexMenu.toString}${id}`}
                                                        disablePadding
                                                        secondaryAction={(
                                                            <IconButton
                                                                onClick={
                                                                    () => {
                                                                        handleSetUnorder(
                                                                            indexMenu,
                                                                            index
                                                                        );
                                                                    }
                                                                }
                                                            >
                                                                <Close
                                                                    aria-label="remove"
                                                                />
                                                            </IconButton>
                                                        )}
                                                    >
                                                        <ListItemText sx={{
                                                            paddingLeft: '1rem'
                                                        }}
                                                        >
                                                            { getStructureName(id) }
                                                        </ListItemText>
                                                    </ListItem>
                                                )) }
                                            </List>
                                        </Paper>
                                    ) }
                            </Box>
                        )) }
                        <List>
                            { unsorted.map((item: IUnordered, index: number) => (
                                <Paper
                                    sx={{
                                        marginY: '0.2rem',
                                        width: '100%'
                                    }}
                                    key={`unorder-${item.id}`}
                                >
                                    <ListItem
                                        sx={{ width: '100%' }}
                                        secondaryAction={(
                                            <IconButton
                                                aria-label="remove"
                                                onClick={
                                                    () => {
                                                        handleDialogOpen(item.id, index);
                                                    }
                                                }
                                            >
                                                <MoreVert />
                                            </IconButton>
                                        )}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '1.1rem',
                                                width: '80%'

                                            }}
                                            gutterBottom
                                        >
                                            {item.name}
                                        </Typography>
                                    </ListItem>
                                </Paper>
                            )) }
                        </List>
                    </Paper>
                </Grid>
            </Grid>
            <Dialog
                open={dialogOpen}
                onClose={handleClose}
            >
                <DialogTitle> Menú </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Seleccione el submenú al que estará asignada esta base de información
                    </DialogContentText>
                    <List>
                        { submenus.map((submenu: ISubmenu, index: number) => (
                            <ListItem
                                key={`selector-${submenu.name}-${index.toString()}`}
                                disablePadding
                            >
                                <ListItemButton onClick={
                                    () => {
                                        handleSelectSubmenu(index);
                                    }
                                }
                                >
                                    <ListItemText>
                                        { submenu.name }
                                    </ListItemText>
                                </ListItemButton>
                            </ListItem>
                        )) }
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cerrar</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={tooltipOpen}
                onClose={handleTooltipClose}
            >
                <DialogTitle> Tooltip </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Introduzca texto de ayuda
                    </DialogContentText>
                    <TextField
                        fullWidth
                        variant="outlined"
                        label="Tooltip"
                        sx={{
                            margin: '0.5rem 0'
                        }}
                        value={tooltipInput}
                        onChange={handleTooltipInputChange}
                    />

                </DialogContent>
                <DialogActions>
                    <Button onClick={handlePersistTooltip}>Guardar</Button>
                    <Button onClick={handleTooltipClose}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
export default Submenus;
